import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lightning, X, CurrencyInr, PaperPlaneTilt, UsersThree, UploadSimple, Tag, ClipboardText } from "@phosphor-icons/react";
import { withBackParam } from "@/lib/backUrl";

/**
 * Floating Quick-Actions button — appears bottom-right on every admin page
 * so admins can jump into the most-common workflows (Record Cash / Record
 * Payment / Add Party / Upload / Pricing) without navigating back to the
 * Dashboard. Each shortcut deep-links to Dashboard with a `?open=…` param;
 * Dashboard auto-opens the matching modal.
 */
const SHORTCUTS = [
    { id: "cash",    label: "Cash / Value Recd",       icon: CurrencyInr,     to: "/admin?open=cash",     tone: "emerald" },
    { id: "payout",  label: "Amt. Paid / Value Out",   icon: PaperPlaneTilt,  to: "/admin?open=payout",   tone: "violet"  },
    { id: "party",   label: "Add Party",            icon: UsersThree,      to: "/admin/parties?new=1", tone: "blue"    },
    { id: "upload",  label: "Verify Slips",         icon: UploadSimple,    to: "/admin/uploads?status=pending",       tone: "amber"   },
    { id: "pricing", label: "Add Pricing Rule",     icon: Tag,             to: "/admin/pricing",       tone: "slate"   },
    { id: "reports", label: "Open Reports",         icon: ClipboardText,   to: "/admin/reports",       tone: "slate"   },
];

const toneClasses = {
    emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200",
    violet:  "bg-violet-50 text-violet-700 hover:bg-violet-100 border-violet-200",
    blue:    "bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200",
    amber:   "bg-amber-50 text-amber-800 hover:bg-amber-100 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200",
};

export default function QuickActionsFab() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Sep 2026 (mobile "fog" overlay — real root cause). This FAB is mounted
    // once by AdminLayout and survives every route change, but `open` was
    // never reset on navigation. Only `go()` cleared it, so ANY other
    // navigation — a Super User bottom-nav tab, browser back, or a link
    // inside the open menu — left `open === true` and the z-40 backdrop
    // below (`bg-black/20 backdrop-blur-[1px]`) stayed on screen as a
    // permanent haze over the next page.
    //
    // This is why the overlay-cleanup in SuperUserBottomNav never fixed it:
    // that routine only removes scrims whose class contains `z-50` (the
    // Radix sheet/dialog overlays), and this one is `z-40`. It is also not
    // an orphaned DOM node — React is legitimately re-rendering it because
    // the component still thinks the menu is open — so removing the node
    // could never fix it either. Close the menu on every route change.
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);
    // v1.28.75 — FAB is now visible on EVERY admin page (including the
    // Dashboard) so admins always have one-tap access to Cash / Payout /
    // Add Party from anywhere.

    const go = (to) => {
        setOpen(false);
        // v1.28.54 — Uses the shared `withBackParam` helper (see
        // /app/frontend/src/lib/backUrl.js). Guarantees ActionPill and
        // this FAB capture the current URL identically — a future fix
        // can never update one and miss the other.
        const isDeepLink = to.includes("?open=");
        if (isDeepLink) {
            navigate(withBackParam(to));
        } else {
            navigate(to);
        }
    };

    return (
        <>
            {open && (
                <div
                    className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
                    onClick={() => setOpen(false)}
                    data-testid="quick-actions-overlay"
                />
            )}
            {/* v1.28.75 — Lifted 76 px on mobile so the FAB clears the
                Super User bottom nav bar (h ~ 56 px + safe-area). */}
            <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end gap-2 md:bottom-6 md:right-6" data-testid="quick-actions-fab">
                {open && (
                    <div className="animate-in slide-in-from-bottom-2 fade-in flex flex-col items-end gap-1.5 rounded-lg border border-slate-200 bg-white p-2 shadow-2xl" data-testid="quick-actions-menu">
                        <div className="mb-1 flex w-full items-center justify-between gap-3 border-b border-slate-100 pb-1.5">
                            <span className="pl-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Quick Actions</span>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                data-testid="quick-actions-close"
                            >
                                <X size={12} />
                            </button>
                        </div>
                        {SHORTCUTS.map((s) => {
                            const Icon = s.icon;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => go(s.to)}
                                    data-testid={`quick-action-${s.id}`}
                                    className={`flex w-56 items-center gap-2 rounded-md border px-3 py-2 text-left text-xs font-medium transition-colors ${toneClasses[s.tone]}`}
                                >
                                    <Icon size={14} weight="fill" />
                                    <span>{s.label}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="quick-actions-toggle"
                    aria-label={open ? "Close quick actions" : "Open quick actions"}
                    className={`flex h-14 w-14 items-center justify-center rounded-full shadow-2xl transition-all ${open ? "bg-slate-900 text-white rotate-45" : "bg-amber-400 text-slate-900 hover:bg-amber-500 hover:scale-110"}`}
                >
                    {open ? <X size={22} weight="bold" /> : <Lightning size={22} weight="fill" />}
                </button>
            </div>
        </>
    );
}
